/**
 * LessonLink - 設定モジュール
 */
import { generateClient } from 'aws-amplify/data';
import { fetchUserAttributes } from 'aws-amplify/auth';
import type { Schema } from '../amplify/data/resource';
import {
  showMessage,
  getDefaultDisplayName,
  validateNotificationEmail,
  canAddNotificationEmail,
  isInstructor,
  getEffectiveDuration,
  formatCurrency,
} from './utils';

const INSTRUCTOR_EMAIL = 'shingo.morii.926@gmail.com';

type Client = ReturnType<typeof generateClient<Schema>>;
let client: Client;

function getClient() {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
}

let currentUser: Schema['User']['type'] | null = null;
let currentSetting: Schema['Setting']['type'] | null = null;

/**
 * ログイン後にUserレコードを取得または作成
 */
export async function ensureUserRecord(): Promise<Schema['User']['type'] | null> {
  if (currentUser) return currentUser;

  const attrs = await fetchUserAttributes();
  const email = attrs.email;
  if (!email) return null;

  // 既存ユーザーを検索
  const { data: users } = await getClient().models.User.list({
    filter: { email: { eq: email } },
  });

  if (users && users.length > 0) {
    currentUser = users[0];
    return currentUser;
  }

  // 新規作成
  const displayName = getDefaultDisplayName(email);
  const role = email === INSTRUCTOR_EMAIL ? 'instructor' : 'student';

  const { data: created } = await getClient().models.User.create({
    email,
    displayName,
    role,
    notificationEmails: [email],
  });

  if (created) {
    currentUser = created;
  }
  return currentUser;
}

/**
 * ユーザー表示名を取得（連絡帳ラベル用）
 */
export async function getUserDisplayNames(): Promise<{ instructor: string; student: string }> {
  try {
    const { data } = await getClient().models.User.list();
    const instructor = data?.find((u) => u.role === 'instructor');
    const student = data?.find((u) => u.role === 'student');
    return {
      instructor: instructor?.displayName || '講師',
      student: student?.displayName || '生徒',
    };
  } catch {
    return { instructor: '講師', student: '生徒' };
  }
}

/**
 * ヘッダーに表示名を設定
 */
export function updateHeaderDisplayName() {
  const subtitle = document.getElementById('header-subtitle') as HTMLElement;
  if (currentUser && subtitle) {
    subtitle.textContent = `${currentUser.displayName}さん`;
  }
}

/**
 * ログアウト時のクリーンアップ
 */
export function clearUserState() {
  currentUser = null;
  currentSetting = null;
  const subtitle = document.getElementById('header-subtitle') as HTMLElement;
  if (subtitle) {
    subtitle.textContent = '講師と生徒をつなぐ';
  }
  // ユーザー管理ナビを非表示にリセット
  const navUsersItem = document.getElementById('nav-users-item');
  if (navUsersItem) navUsersItem.style.display = 'none';
}

/**
 * 現在のユーザーのロールを取得
 */
export function getCurrentUserRole(): string | null {
  return currentUser?.role ?? null;
}

/**
 * 現在のユーザーのメールアドレスを取得
 */
export function getCurrentUserEmail(): string | null {
  return currentUser?.email ?? null;
}

/**
 * 設定画面のイベントを初期化
 */
export function initSettings() {
  // 表示名保存
  const displayNameForm = document.getElementById('display-name-form') as HTMLFormElement;
  displayNameForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = document.getElementById('display-name-input') as HTMLInputElement;
    const newName = input.value.trim();
    if (!newName) {
      showMessage('表示名を入力してください');
      return;
    }
    if (!currentUser) return;

    try {
      const { data: updated } = await getClient().models.User.update({
        id: currentUser.id,
        displayName: newName,
      });
      if (updated) {
        currentUser = updated;
        updateHeaderDisplayName();
        showMessage('表示名を保存しました', 'success');
      }
    } catch (error) {
      console.error('Update display name error:', error);
      showMessage('表示名の保存に失敗しました');
    }
  });

  // 通知メール追加ボタン
  const addEmailBtn = document.getElementById('add-email-btn') as HTMLButtonElement;
  const addEmailSection = document.getElementById('add-email-section') as HTMLElement;
  const addEmailCancel = document.getElementById('add-email-cancel') as HTMLButtonElement;
  const addEmailForm = document.getElementById('add-email-form') as HTMLFormElement;

  addEmailBtn.addEventListener('click', () => {
    addEmailSection.classList.remove('hidden');
    addEmailBtn.classList.add('hidden');
    const input = document.getElementById('new-email-input') as HTMLInputElement;
    input.value = '';
    input.focus();
  });

  addEmailCancel.addEventListener('click', () => {
    addEmailSection.classList.add('hidden');
    addEmailBtn.classList.remove('hidden');
    addEmailBtn.focus();
  });

  addEmailForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = document.getElementById('new-email-input') as HTMLInputElement;
    const email = input.value.trim();

    if (!validateNotificationEmail(email)) {
      showMessage('有効なメールアドレスを入力してください');
      return;
    }

    if (!currentUser) return;
    const currentEmails = (currentUser.notificationEmails || []).filter(
      (e): e is string => !!e,
    );

    if (!canAddNotificationEmail(currentEmails.length)) {
      showMessage('通知メールアドレスは最大5件までです');
      return;
    }

    if (currentEmails.includes(email)) {
      showMessage('このメールアドレスは既に登録されています');
      return;
    }

    try {
      const updatedEmails = [...currentEmails, email];
      const { data: updated } = await getClient().models.User.update({
        id: currentUser.id,
        notificationEmails: updatedEmails,
      });
      if (updated) {
        currentUser = updated;
        renderNotificationEmails();
        addEmailSection.classList.add('hidden');
        addEmailBtn.classList.remove('hidden');
        addEmailBtn.focus();
        showMessage('メールアドレスを追加しました', 'success');
      }
    } catch (error) {
      console.error('Add email error:', error);
      showMessage('メールアドレスの追加に失敗しました');
    }
  });

  // 通知メール削除（イベント委譲）
  const emailList = document.getElementById('notification-email-list') as HTMLElement;
  emailList.addEventListener('click', async (e) => {
    const deleteBtn = (e.target as HTMLElement).closest(
      '[data-delete-email]',
    ) as HTMLElement;
    if (!deleteBtn || !currentUser) return;

    const emailToDelete = deleteBtn.dataset.deleteEmail!;
    const currentEmails = (currentUser.notificationEmails || []).filter(
      (em): em is string => !!em,
    );
    const updatedEmails = currentEmails.filter((em) => em !== emailToDelete);

    try {
      const { data: updated } = await getClient().models.User.update({
        id: currentUser.id,
        notificationEmails: updatedEmails,
      });
      if (updated) {
        currentUser = updated;
        renderNotificationEmails();
        showMessage('メールアドレスを削除しました', 'success');
        addEmailBtn.focus();
      }
    } catch (error) {
      console.error('Delete email error:', error);
      showMessage('メールアドレスの削除に失敗しました');
    }
  });

  // Meet URL 保存
  const meetUrlForm = document.getElementById('meet-url-form') as HTMLFormElement;
  meetUrlForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = document.getElementById('meet-url-input') as HTMLInputElement;
    const url = input.value.trim();

    try {
      if (currentSetting) {
        const { data: updated } = await getClient().models.Setting.update({
          id: currentSetting.id,
          meetUrl: url || null,
        });
        if (updated) currentSetting = updated;
      } else {
        const { data: created } = await getClient().models.Setting.create({
          meetUrl: url || null,
        });
        if (created) currentSetting = created;
      }
      showMessage('Google Meet URLを保存しました', 'success');
    } catch (error) {
      console.error('Save meet URL error:', error);
      showMessage('Google Meet URLの保存に失敗しました');
    }
  });
}

/**
 * 設定画面のデータを読み込む
 */
export async function loadSettings() {
  if (!currentUser) {
    await ensureUserRecord();
  }

  // 表示名
  const displayNameInput = document.getElementById('display-name-input') as HTMLInputElement;
  if (currentUser) {
    displayNameInput.value = currentUser.displayName;
  }

  // 通知メール
  renderNotificationEmails();

  // 共通Meet URLセクションは常に非表示（生徒別に移行済み）
  const commonMeetSection = document.getElementById('common-meet-url-section') as HTMLElement;
  commonMeetSection.classList.add('hidden');

  const studentSettingsSection = document.getElementById('student-settings-section') as HTMLElement;
  const myLessonSettingsSection = document.getElementById('my-lesson-settings-section') as HTMLElement;

  if (isInstructor(currentUser?.role)) {
    // 講師: 生徒別設定を表示
    studentSettingsSection.classList.remove('hidden');
    myLessonSettingsSection.classList.add('hidden');
    await renderStudentSettings();
  } else {
    // 生徒: わたしの授業設定を表示
    studentSettingsSection.classList.add('hidden');
    myLessonSettingsSection.classList.remove('hidden');
    renderMyLessonSettings();
  }
}

/**
 * 生徒ごとの授業時間を取得（schedule.tsで使用）
 */
export function getStudentDuration(email: string): number {
  return studentDurationMap.get(email) ?? 30;
}

// email → lessonDurationMinutes のキャッシュ（schedule.tsで参照）
const studentDurationMap: Map<string, number> = new Map();

/**
 * 生徒の授業設定を一括ロード（schedule.tsから呼ばれる）
 */
export async function loadStudentSettings(): Promise<Map<string, { duration: number; price: number }>> {
  const result = new Map<string, { duration: number; price: number }>();
  try {
    const { data: users } = await getClient().models.User.list();
    users?.filter((u) => u.role === 'student').forEach((u) => {
      const duration = getEffectiveDuration(u.lessonDurationMinutes);
      const price = u.pricePerLesson ?? 1200;
      result.set(u.email, { duration, price });
      studentDurationMap.set(u.email, duration);
    });
  } catch (error) {
    console.error('Load student settings error:', error);
  }
  return result;
}

/**
 * 講師画面: 生徒別設定を表示
 */
async function renderStudentSettings() {
  const container = document.getElementById('student-settings-list') as HTMLElement;
  try {
    const { data: users } = await getClient().models.User.list();
    const students = users?.filter((u) => u.role === 'student') || [];

    if (students.length === 0) {
      container.innerHTML = '<p class="text-gray-500">生徒がいません</p>';
      return;
    }

    container.innerHTML = students
      .map((s) => {
        const escapedEmail = s.email.replace(/"/g, '&quot;');
        const duration = getEffectiveDuration(s.lessonDurationMinutes);
        const price = s.pricePerLesson ?? 1200;
        const meetUrl = s.meetUrl || '';
        const escapedMeetUrl = meetUrl.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `<details class="border border-gray-200 rounded mb-2">
          <summary class="p-3 bg-gray-50 cursor-pointer">${s.displayName}さん</summary>
          <div class="p-3 space-y-3" data-student-id="${s.id}" data-student-email="${escapedEmail}">
            <div>
              <label class="block text-sm mb-1">授業時間</label>
              <select class="student-duration border border-gray-300 rounded px-2 py-2">
                <option value="30" ${duration === 30 ? 'selected' : ''}>30分</option>
                <option value="60" ${duration === 60 ? 'selected' : ''}>60分</option>
              </select>
            </div>
            <div>
              <label class="block text-sm mb-1">授業単価（円）</label>
              <input type="number" class="student-price border border-gray-300 rounded px-3 py-2 w-full" value="${price}" min="0" step="100">
            </div>
            <div>
              <label class="block text-sm mb-1">Meet URL</label>
              <input type="text" class="student-meet-url border border-gray-300 rounded px-3 py-2 w-full" value="${escapedMeetUrl}" placeholder="https://meet.google.com/xxx-xxxx-xxx">
            </div>
            <button type="button" class="save-student-settings bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">保存</button>
          </div>
        </details>`;
      })
      .join('');

    // 保存ボタンのイベント委譲
    container.addEventListener('click', async (e) => {
      const btn = (e.target as HTMLElement).closest('.save-student-settings') as HTMLElement;
      if (!btn) return;

      const wrapper = btn.closest('[data-student-id]') as HTMLElement;
      const studentId = wrapper.dataset.studentId!;

      const durationSelect = wrapper.querySelector('.student-duration') as HTMLSelectElement;
      const priceInput = wrapper.querySelector('.student-price') as HTMLInputElement;
      const meetUrlInput = wrapper.querySelector('.student-meet-url') as HTMLInputElement;

      const newDuration = parseInt(durationSelect.value);
      const newPrice = parseInt(priceInput.value) || 1200;
      const newMeetUrl = meetUrlInput.value.trim();

      try {
        await getClient().models.User.update({
          id: studentId,
          lessonDurationMinutes: newDuration,
          pricePerLesson: newPrice,
          meetUrl: newMeetUrl || null,
        });
        // キャッシュ更新
        const email = wrapper.dataset.studentEmail!;
        studentDurationMap.set(email, newDuration);
        showMessage('生徒設定を保存しました', 'success');
      } catch (error) {
        console.error('Save student settings error:', error);
        showMessage('生徒設定の保存に失敗しました');
      }
    });
  } catch (error) {
    console.error('Render student settings error:', error);
    container.innerHTML = '<p class="text-red-600">生徒設定の読み込みに失敗しました</p>';
  }
}

/**
 * 生徒画面: わたしの授業設定を表示（閲覧のみ）
 */
function renderMyLessonSettings() {
  if (!currentUser) return;
  const durationEl = document.getElementById('my-lesson-duration') as HTMLElement;
  const priceEl = document.getElementById('my-lesson-price') as HTMLElement;
  const meetUrlEl = document.getElementById('my-lesson-meet-url') as HTMLElement;

  const duration = getEffectiveDuration(currentUser.lessonDurationMinutes);
  const price = currentUser.pricePerLesson ?? 1200;
  const meetUrl = currentUser.meetUrl;

  durationEl.textContent = `${duration}分`;
  priceEl.textContent = `${formatCurrency(price)}円`;

  if (meetUrl) {
    const escaped = meetUrl.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    meetUrlEl.innerHTML = `<a href="${escaped}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${escaped}</a>`;
  } else {
    meetUrlEl.textContent = '未設定（講師に確認してください）';
  }
}

function renderNotificationEmails() {
  const container = document.getElementById('notification-email-list') as HTMLElement;
  const addBtn = document.getElementById('add-email-btn') as HTMLElement;
  if (!currentUser) return;

  const emails = (currentUser.notificationEmails || []).filter(
    (e): e is string => !!e,
  );
  const loginEmail = currentUser.email;

  container.innerHTML = emails
    .map((email, index) => {
      const isLoginEmail = email === loginEmail;
      const escaped = email
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      return `<li class="flex items-center justify-between py-2${index > 0 ? ' border-t border-gray-200' : ''}">
        <span>${escaped}${isLoginEmail ? '（ログイン用）' : ''}</span>
        ${isLoginEmail ? '' : `<button type="button" data-delete-email="${escaped}" class="text-red-600 hover:text-red-800 text-sm" aria-label="${escaped}を削除">削除</button>`}
      </li>`;
    })
    .join('');

  // 追加ボタンの表示/非表示
  if (canAddNotificationEmail(emails.length)) {
    addBtn.classList.remove('hidden');
  } else {
    addBtn.classList.add('hidden');
  }
}
